import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createItem, uploadItemImage, getCategories } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, Image as ImageIcon, DollarSign, Check } from 'lucide-react';
import { toast } from 'sonner';

const ListItemPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price_hourly: '',
    price_daily: '',
    price_weekly: '',
    condition: 'good',
    security_deposit: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please log in to list an item');
      navigate('/login');
      return;
    }
    
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages(prev => [...prev, { file, preview: event.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.price_daily) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create item
      const itemData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price_hourly: formData.price_hourly ? parseFloat(formData.price_hourly) : null,
        price_daily: parseFloat(formData.price_daily),
        price_weekly: formData.price_weekly ? parseFloat(formData.price_weekly) : null,
        condition: formData.condition,
        security_deposit: formData.security_deposit ? parseFloat(formData.security_deposit) : null
      };
      
      const item = await createItem(itemData);
      
      // Upload images
      for (const img of images) {
        try {
          await uploadItemImage(item.id, img.file);
        } catch (imgError) {
          console.error('Failed to upload image:', imgError);
        }
      }
      
      toast.success('Item listed successfully!');
      navigate(`/items/${item.id}`);
    } catch (error) {
      console.error('Failed to create item:', error);
      toast.error(error.response?.data?.detail || 'Failed to list item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-semibold text-[#2C3329] mb-2">
          List an Item
        </h1>
        <p className="text-[#596652] mb-8">Share your items with your neighbors and earn money</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Images */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">
              <ImageIcon className="w-5 h-5 inline mr-2" />
              Photos
            </h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {images.map((img, index) => (
                <div key={img.preview || `img-${index}`} className="relative aspect-square rounded-xl overflow-hidden border border-[#EBE8DF]">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                    data-testid={`remove-image-${index}`}
                  >
                    <X className="w-4 h-4 text-[#E53935]" />
                  </button>
                </div>
              ))}
              
              {images.length < 5 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-[#EBE8DF] flex flex-col items-center justify-center cursor-pointer hover:border-[#8C9A76] hover:bg-[#8C9A76]/5 transition-all" data-testid="upload-image-area">
                  <Upload className="w-6 h-6 text-[#596652] mb-2" />
                  <span className="text-xs text-[#596652]">Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-input"
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-[#596652] mt-3">Add up to 5 photos. First photo will be the cover.</p>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">
              Basic Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-[#2C3329]">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="e.g., DeWalt 20V Cordless Drill"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-[#2C3329]">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="Describe your item, its features, and any usage instructions..."
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category" className="text-[#2C3329]">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1 border-[#EBE8DF] rounded-xl" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="condition" className="text-[#2C3329]">Condition</Label>
                  <Select 
                    value={formData.condition} 
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger className="mt-1 border-[#EBE8DF] rounded-xl" data-testid="condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="like_new">Like New</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">
              <DollarSign className="w-5 h-5 inline mr-2" />
              Pricing
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="price_hourly" className="text-[#2C3329]">Hourly Rate</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#596652]">$</span>
                  <Input
                    id="price_hourly"
                    name="price_hourly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_hourly}
                    onChange={handleChange}
                    className="pl-7 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="0.00"
                    data-testid="price-hourly-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="price_daily" className="text-[#2C3329]">Daily Rate *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#596652]">$</span>
                  <Input
                    id="price_daily"
                    name="price_daily"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_daily}
                    onChange={handleChange}
                    required
                    className="pl-7 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="0.00"
                    data-testid="price-daily-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="price_weekly" className="text-[#2C3329]">Weekly Rate</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#596652]">$</span>
                  <Input
                    id="price_weekly"
                    name="price_weekly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_weekly}
                    onChange={handleChange}
                    className="pl-7 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="0.00"
                    data-testid="price-weekly-input"
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-[#596652] mt-3">Only daily rate is required. Offer hourly/weekly rates for more flexibility.</p>

            {/* Security Deposit */}
            <div className="mt-6 pt-5 border-t border-[#EBE8DF]">
              <Label htmlFor="security_deposit" className="text-[#2C3329] font-medium">Security Deposit (optional)</Label>
              <p className="text-xs text-[#596652] mb-2">Require a refundable deposit for high-value items.</p>
              <div className="relative max-w-[200px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#596652]">$</span>
                <Input
                  id="security_deposit"
                  name="security_deposit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.security_deposit}
                  onChange={handleChange}
                  className="pl-7 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="0.00"
                  data-testid="security-deposit-input"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 border-[#EBE8DF] rounded-full"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
              data-testid="submit-item-button"
            >
              {loading ? (
                'Creating...'
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  List Item
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListItemPage;
